import { blogPosts, type BlogPost } from "./posts";
import { localPreviewOrigin } from "./origin";

export const blogTitle = "Tattoo culture & ideas | The blog";
export const blogDescription = "Tattoo culture, creative inspiration, and practical ideas from Stencil Studio.";
export const publicSiteOrigin = import.meta.env.VITE_PUBLIC_SITE_URL || "";
const base = import.meta.env.BASE_URL;

export type SeoTag = { tag: "meta" | "link" | "script"; attributes: Record<string, string>; text?: string };
export interface BlogSeo { title: string; tags: SeoTag[] }

export function publicUrl(path: string, origin = publicSiteOrigin): string {
  return new URL(`${base}${path.replace(/^\//, "")}`, `${origin}/`).href;
}

export function sortedPosts() {
  return [...blogPosts].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

/** A single descriptor supplies the initial HTML and subsequent client navigation. */
export function getBlogSeo(path: string, origin = publicSiteOrigin || (import.meta.env.DEV && typeof window !== "undefined" ? localPreviewOrigin(window.location.origin) : "")): BlogSeo {
  const normalized = path.replace(/\/+$/, "");
  const hub = normalized === "/blog";
  const post = blogPosts.find(post => normalized === `/blog/${post.slug}`);
  const found = hub || Boolean(post);
  const title = `${hub ? blogTitle : post?.title ?? "Article not found"} · Stencil Studio`;
  const description = hub ? blogDescription : post?.excerpt ?? "Explore the latest articles from Stencil Studio.";
  const meta = (name: string, content: string): SeoTag => ({ tag: "meta", attributes: { name, content } });
  const property = (name: string, content: string): SeoTag => ({ tag: "meta", attributes: { property: name, content } });
  const tags: SeoTag[] = [
    meta("description", description),
    meta("robots", found && origin && !localPreviewOrigin(origin) ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" : "noindex, follow"),
    property("og:type", post ? "article" : "website"),
    property("og:site_name", "Stencil Studio"),
    property("og:title", title),
    property("og:description", description),
    meta("twitter:card", "summary_large_image"),
    meta("twitter:title", title),
    meta("twitter:description", description),
  ];
  // A missing deployment origin must never publish a guessed production canonical.
  if (!found || !origin) return { title, tags };

  const url = publicUrl(normalized, origin);
  const home = publicUrl("", origin);
  const blog = publicUrl("blog", origin);
  const cover = post?.cover ?? sortedPosts()[0]?.cover;
  const image = cover ? new URL(cover.src, `${origin}/`).href : publicUrl("brand/stencil-studio-mark.webp", origin);
  tags.push(
    { tag: "link", attributes: { rel: "canonical", href: url } },
    { tag: "link", attributes: { rel: "alternate", hreflang: "en", href: url } },
    { tag: "link", attributes: { rel: "alternate", hreflang: "x-default", href: url } },
    property("og:url", url), property("og:image", image), property("og:image:alt", cover?.alt ?? "Stencil Studio"),
    meta("twitter:image", image), meta("twitter:image:alt", cover?.alt ?? "Stencil Studio"),
  );
  if (post) {
    tags.push(meta("author", post.author), property("article:published_time", `${post.publishedAt}T00:00:00Z`), property("article:section", post.category));
    if (post.updatedAt) tags.push(property("article:modified_time", `${post.updatedAt}T00:00:00Z`));
  }

  const organization = { "@type": "Organization", "@id": `${home}#organization`, name: "Stencil Studio", url: home, logo: { "@type": "ImageObject", url: publicUrl("brand/stencil-studio-mark.webp", origin) } };
  const website = { "@type": "WebSite", "@id": `${home}#website`, url: home, name: "Stencil Studio", inLanguage: "en", publisher: { "@id": organization["@id"] } };
  const crumbs = [{ name: "Home", url: home }, { name: "Blog", url: blog }, ...(post ? [{ name: post.title, url }] : [])];
  const breadcrumb = { "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`, itemListElement: crumbs.map((crumb, index) => ({ "@type": "ListItem", position: index + 1, name: crumb.name, item: crumb.url })) };
  const webpage = {
    "@type": post ? "WebPage" : "CollectionPage", "@id": `${url}#webpage`, url, name: title, description, inLanguage: "en",
    isPartOf: { "@id": website["@id"] }, breadcrumb: { "@id": breadcrumb["@id"] }, mainEntity: { "@id": `${url}${post ? "#article" : "#posts"}` },
  };
  const graph: Record<string, unknown>[] = [organization, website, breadcrumb, webpage];
  if (post) {
    const article: Record<string, unknown> = {
      "@type": "BlogPosting", "@id": `${url}#article`, url, headline: post.title, description: post.excerpt,
      inLanguage: "en", datePublished: `${post.publishedAt}T00:00:00Z`, isAccessibleForFree: true,
      ...(post.updatedAt ? { dateModified: `${post.updatedAt}T00:00:00Z` } : {}),
      author: { "@type": post.authorType ?? "Person", name: post.author, ...(post.authorUrl ? { url: post.authorUrl } : {}) },
      publisher: { "@id": organization["@id"] }, mainEntityOfPage: { "@id": webpage["@id"] },
      image: { "@type": "ImageObject", url: image, caption: post.cover.caption }, articleSection: post.category,
      citation: post.sources.map(source => source.href),
    };
    if (post.faq?.length) {
      article.hasPart = { "@id": `${url}#faq` };
      graph.push({ "@type": "FAQPage", "@id": `${url}#faq`, url: `${url}#faq`, inLanguage: "en", isPartOf: { "@id": webpage["@id"] }, mainEntity: post.faq.map(faq => ({ "@type": "Question", name: faq.question, acceptedAnswer: { "@type": "Answer", text: faq.answer } })) });
    }
    graph.push(article);
  } else {
    graph.push({ "@type": "ItemList", "@id": `${url}#posts`, numberOfItems: blogPosts.length, itemListElement: sortedPosts().map((post, index) => ({ "@type": "ListItem", position: index + 1, name: post.title, url: publicUrl(`blog/${post.slug}`, origin) })) });
  }
  tags.push({ tag: "script", attributes: { type: "application/ld+json", id: "blog-structured-data" }, text: JSON.stringify({ "@context": "https://schema.org", "@graph": graph }).replace(/</g, "\\u003c") });
  return { title, tags };
}

export function postPath(post: BlogPost) { return `/blog/${post.slug}`; }
